import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetInboxes } from '@common/hooks/useInboxes';
import { IProShipmentApiResponse } from '@packlink/packlink-sdk';
import { AppDispatch } from '@store';
import { handleDeleteShipmentEvent, handleShipments } from '@store/actions/bulk';
import { getClientIdentifier } from '@store/selectors/client';
import { EventName } from '@types';
import { ShipmentUtils } from '@utils/ShipmentUtils';
import { useEvent } from '@packlink/event-handler';

export function useShipmentEvents() {
    const dispatch = useDispatch<AppDispatch>();
    const clientId = useSelector(getClientIdentifier);

    const { eventBind: deleteEventBind, eventUnbind: deleteEventUnbind } = useEvent<IProShipmentApiResponse>(
        clientId,
        EventName.SHIPMENT_DELETED,
    );
    const { eventBind: updateEventBind, eventUnbind: updateEventUnbind } = useEvent<IProShipmentApiResponse>(
        clientId,
        EventName.SHIPMENT_UPDATED,
    );
    const { eventBind: createEventBind, eventUnbind: createEventUnbind } = useEvent<IProShipmentApiResponse>(
        clientId,
        EventName.SHIPMENT_CREATED,
    );

    const { refetchInboxesDebounced } = useGetInboxes();

    useEffect(() => {
        const handleShipmentDeleted = (data: IProShipmentApiResponse) => {
            dispatch(handleDeleteShipmentEvent(data));
            refetchInboxesDebounced();
        };

        const handleShipmentUpdated = (shipment: IProShipmentApiResponse) => {
            const shipmentSdk = ShipmentUtils.proShipmentCreateFromResponse(shipment);
            dispatch(handleShipments([shipmentSdk], true));
            refetchInboxesDebounced();
        };

        createEventBind(handleShipmentUpdated);
        updateEventBind(handleShipmentUpdated);
        deleteEventBind(handleShipmentDeleted);

        return () => {
            createEventUnbind(handleShipmentUpdated);
            updateEventUnbind(handleShipmentUpdated);
            deleteEventUnbind(handleShipmentDeleted);
        };
    }, [
        dispatch,
        deleteEventBind,
        deleteEventUnbind,
        updateEventBind,
        updateEventUnbind,
        createEventBind,
        createEventUnbind,
        refetchInboxesDebounced,
    ]);
}
